<template>
  <div>
    <div
      class="
        d-flex
        pb-6
        align-center
      "
    >
      <h2
        :class="titleClassname"
      >{{ title }}</h2>

      <v-spacer></v-spacer>

      <TableSearch
        label="Search products"
        v-model="search"
        @input="reload({ reset: true })"
      ></TableSearch>

      <v-switch
        v-model="showDisabled"
        label="Show disabled"
        class="pt-0 mt-0 ml-6"
        hide-details
        @change="reload({ reset: true })"
      ></v-switch>

      <NewButton
        to-name="new-product"
        class="ml-6"
      >Create new product</NewButton>
    </div>

    <v-data-table
      :headers="headers"
      :items="_products"
      :page="page"
      :items-per-page="itemsPerPage"
      :sort-by.sync="sortBy"
      @update:sort-by="reload"
      :sort-desc.sync="sortDesc"
      @update:sort-desc="reload"
      multi-sort
      :server-items-length="total"
      hide-default-footer
      :loading="loading"
      @page-count="pageCount = $event"
      @click:row="goToProduct"
    >
      <template v-slot:item.updated_at="{ item }">
        <span
          class="text-no-wrap"
          :id="`focus_${item.id}`"
        >
          {{ new Date(item.updated_at).toLocaleDateString() }}
        </span>
      </template>

      <template v-slot:item.created_at="{ item }">
        <span class="text-no-wrap">
          {{ new Date(item.created_at).toLocaleDateString() }}
        </span>
      </template>

      <template v-slot:item.status="{ item }">
        <span
          class="font-weight-medium"
          :class="item.status_classname"
        >{{ item.human_status }}</span>
      </template>

      <template v-slot:item.price="{ item }">
        {{ item.price | monetize('EUR') }}
      </template>

      <template
        v-if="scope.pageId"
        v-slot:item.rank="{ item }"
      >
        <div class="d-flex justify-end">
          <v-text-field
            dense
            :value="item.rank"
            type="number"
            @input="updateRank(item, $event)"
            @click.stop
            outlined
            hide-details
            rounded
            style="width: 70px"
          ></v-text-field>
        </div>
      </template>
    </v-data-table>

    <div
      class="py-6"
    >
      <TablePagination
        v-model="page"
        @input="reload"
        :itemsPerPage.sync="itemsPerPage"
        @update:itemsPerPage="reload"
        :pageCount.sync="pageCount"
        :total="total"
      ></TablePagination>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import TableSearch from './TableSearch.vue'
import NewButton from './NewButton.vue'
import TablePagination from './TablePagination.vue'

let timeout

export default {
  name: 'ProductsList',
  components: {
    TableSearch,
    NewButton,
    TablePagination
  },
  props: {
    titleClassname: {
      type: String,
      default: 'headline'
    },
    title: {
      type: String,
      required: true
    },
    scope: {
      type: Object,
      required: false,
      default() { return {} }
    }
  },
  data() {
    const [scopeName, scopeId] = Object.entries(this.scope)[0] || []
    const storagePrefix = ['products', scopeName, scopeId].filter(Boolean).join(':')
    const { itemsPerPage, page, sortBy, sortDesc, search, showDisabled } = this.$pagination.getOptions(storagePrefix)

    const rankHeader = this.scope.pageId ? [{ text: 'Rank', value: 'rank' }] : []

    return {
      storagePrefix,
      search,
      pageCount: 0,
      showDisabled,
      page,
      loading: true,
      itemsPerPage,
      sortBy,
      sortDesc,
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Brand', value: 'brand_name' },
        { text: 'Title', value: 'title' },
        { text: 'Status', value: 'status' },
        { text: 'Price', value: 'price' },
        { text: 'State', value: 'state' },
        { text: 'Last update', value: 'updated_at' },
        { text: 'Creation date', value: 'created_at' },
        ...rankHeader
      ]
    }
  },
  computed: {
    ...mapState('Products', ['products', 'total']),
    _products() {
      return this.products.map((product) => {
        const { flux_status, status } = product
        product.status_classname = flux_status === 'disabled' || ['disabled', 'manually_disabled'].includes(status)
          ? 'red--text'
          : 'green--text'

        const statusKey = flux_status === 'disabled'
          ? 'PRODUCT_DISABLED_FLUX'
          : `PRODUCT_${status.toUpperCase()}`
        product.human_status = this.$translate.instant(statusKey)

        return product
      })
    }
  },
  created() {
    this.reload()
  },
  methods: {
    goToProduct(product) {
      const query = {}
      if (this.$route.name !== 'home') {
        query.from = this.$route.name
        query.id = this.$route.params.id
      }
      const params = { id: product.id }
      this.$router.push({ name: 'product', params, query })
    },
    async reload({ reset } = {}) {
      if (reset) this.page = 1
      if (timeout) clearTimeout(timeout)
      this.loading = true
      timeout = setTimeout(async () => {
        const params = {
          itemsPerPage: this.itemsPerPage,
          page: this.page,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          search: this.search,
          showDisabled: this.showDisabled,
          scope: this.scope
        }
        await this.$store.dispatch('Products/getAll', params)

        timeout = null
        this.loading = false

        delete params.scope
        this.$pagination.update(params, this.storagePrefix)
        this.$pagination.focus()
      }, 300)
    },
    async updateRank(product, rank) {
      const params = {
        productId: product.id,
        rank
      }
      await this.$store.dispatch('Pages/updatePageProductRank', params)
      product.rank = rank
    }
  }
}
</script>
